import { invalidate } from '@react-three/fiber';
import { toastNotifications } from '@uag/react-core';
import { t, type TFunction } from 'i18next';
import {
    Box3,
    BufferAttribute,
    BufferGeometry,
    Matrix4 as Matrix4Three,
    Mesh,
    Plane,
    Quaternion,
    Ray,
    Vector3,
    type WebGLRenderer
} from 'three';

import { PartPlacement } from 'api/models';
import {
    cloneInstanceParts,
    getArticleId,
    isFramiClamp,
    mapPartsArrayToRecord,
    updateFullQuaternion
} from 'app/modules/wallforming/components/scenes/objects/articles/utils/articleUtils';
import { decomposeRollAroundAxis } from 'app/modules/wallforming/components/scenes/objects/articles/utils/transformationUtils';
import { WorkingPlaneData } from 'app/modules/wallforming/components/scenes/objects/helpers/types/workingPlaneData';
import { POIFilterType } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiFilterType';
import {
    getRelatedPoiIndices,
    isPlanarFacePoi
} from 'app/modules/wallforming/components/scenes/objects/pois/utils/poiUtils';
import { InteractionMode } from 'app/modules/wallforming/components/scenes/types/interactionMode';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { syncDragStateOnPoiChange } from 'app/modules/wallforming/components/state-management/placement/slices/articles/articleEventHandlerSlice';
import { type DraggingBoxWorkerInput, postDraggingBoxTask } from 'app/modules/wallforming/components/workers';
import { EMPTY_GUID, EPSILON } from 'shared/constants';

import type { ArticleDragReference } from 'app/modules/wallforming/components/scenes/objects/articles/types/articleDragReference';
import type { ArticleInstance } from 'app/modules/wallforming/components/scenes/objects/articles/types/articleInstance';
import type { Part } from 'app/modules/wallforming/components/scenes/objects/articles/types/part';
import type { DraggingBox } from 'app/modules/wallforming/components/scenes/objects/helpers/types/draggingBox';
import type { DraggingBoxStatus } from 'app/modules/wallforming/components/scenes/objects/helpers/types/draggingBoxStatus';
import type { PlacementState } from 'app/modules/wallforming/components/state-management/placement/types';
import type { RefObject } from 'react';

/* variables */
const ALIGNMENT_PARALLEL_THRESHOLD = 0.999; // Threshold for parallel vectors

const TOP_BOTTOM_VIEW_THRESHOLD = 0.95; // cos of ~18°

export const instanceToArticleIdMap = new Map<string, string>();
export const instanceToPoiIndexMap = new Map<string, number>();

export const draggingBoxStatus: DraggingBoxStatus = {
    isComputed: false,
    isPending: false
};

export const dragColliderCache = new Map<string, Vector3[]>();
export const perInstanceDragOffsets = new Map<string, Vector3>();
export const perInstancePlanes = new Map<string, Plane>();
export const perInstancePrevIntersections = new Map<string, Vector3>();

export const dragDelta = new Vector3();
export const anchorPosition = new Vector3();
export const targetPosition = new Vector3();
export const initialPoint = new Vector3();
export const adjustedSnapSource = new Vector3();

export const raycastPlane = new Plane();
export const dragAnchorPositionRef: { current: Vector3 | null } = { current: null };

export const activePoiGuids = new Set<string>();

const instanceDragDelta = new Vector3();
const perInstancePosition = new Vector3();
const perInstanceAlignment = new Vector3();
const projectedSnapDelta = new Vector3();
const wallIntersectionPoint = new Vector3();
const wallCenter = new Vector3();
const boundingBoxHelper = new Box3();

/* dragging */
export function getAnchorPosition(instance: ArticleInstance, target: Vector3): boolean {
    const parts = Object.values(instance.parts);
    if (parts.length === 0) {
        return false;
    }

    target.set(0, 0, 0);
    for (const part of parts) {
        target.add(part.position);
    }

    target.divideScalar(parts.length);
    return true;
}

export function shouldUseZAxisOnlyDrag(selectedInstanceIds: readonly string[], state: PlacementState): boolean {
    if (selectedInstanceIds.length === 0) {
        return false;
    }

    const poiGuidToFilterType = new Map<string, POIFilterType>();
    for (const poiData of state.pois) {
        if (poiData.filterType !== undefined) {
            poiGuidToFilterType.set(poiData.poi.poiGuid, poiData.filterType);
        }
    }

    const findInstance = (instanceId: string): ArticleInstance | undefined => {
        const articleId = instanceToArticleIdMap.get(instanceId);
        if (articleId) {
            const instance = state.articles[articleId]?.articleInstances[instanceId];
            if (instance) {
                return instance;
            }
        }
        for (const article of Object.values(state.articles)) {
            const instance = article.articleInstances[instanceId];
            if (instance) {
                return instance;
            }
        }
        return undefined;
    };

    let referencePoiGuids: Set<string> | null = null;

    for (const instanceId of selectedInstanceIds) {
        const instanceData = findInstance(instanceId);
        const poiGuids = (instanceData?.poiGuids ?? []).filter((guid) => guid && guid !== EMPTY_GUID);

        if (poiGuids.some((guid) => poiGuidToFilterType.get(guid) === POIFilterType.Corner)) {
            return true;
        }

        const currentPoiGuids = new Set(poiGuids);
        if (!referencePoiGuids) {
            referencePoiGuids = currentPoiGuids;
            continue;
        }

        if (
            currentPoiGuids.size !== referencePoiGuids.size ||
            [...currentPoiGuids].some((guid) => !referencePoiGuids!.has(guid))
        ) {
            return true;
        }
    }

    return false;
}

export function getAnchorAlignment(instance: ArticleInstance, target: Vector3): boolean {
    const parts = Object.values(instance.parts);
    if (parts.length === 0) {
        return false;
    }

    target.set(0, 0, 0);
    for (const part of parts) {
        target.add(part.alignment ?? new Vector3(0, 0, 1));
    }

    target.normalize();
    return true;
}

export function getAnchorDragContext(
    anchorId: string | undefined,
    dragRefsMap: Map<string, ArticleDragReference>
): { anchorArticleId?: string; anchorDragRef?: ArticleDragReference; anchorInstanceData?: ArticleInstance } {
    if (!anchorId) {
        return {};
    }

    const anchorArticleId = instanceToArticleIdMap.get(anchorId);
    const anchorDragRef = anchorArticleId ? dragRefsMap.get(anchorArticleId) : undefined;
    const anchorInstanceData = anchorDragRef?.articleRef.current?.articleInstances[anchorId];

    return { anchorArticleId, anchorDragRef, anchorInstanceData };
}

export function getAnchorPart(instance: ArticleInstance | null | undefined): Part | null {
    if (!instance) {
        return null;
    }

    const partKeys = Object.keys(instance.parts);
    if (partKeys.length === 0) {
        return null;
    }

    const anchorPartKey =
        partKeys.length === 1 ? partKeys[0] : (partKeys.find((key) => key.length === 8) ?? partKeys[0]);

    const anchorPart = instance.parts[anchorPartKey];
    if (!anchorPart) {
        return null;
    }

    return {
        alignment: anchorPart.alignment.clone(),
        position: anchorPart.position.clone(),
        rotation: anchorPart.rotation,
        scale: anchorPart.scale.clone()
    };
}

export function translateInstanceParts(instance: ArticleInstance, delta: Vector3): void {
    for (const part of Object.values(instance.parts)) {
        part.position.add(delta);
    }
}

export function translateDraggingBox(draggingBox: DraggingBox, delta: Vector3): void {
    const e = draggingBox.localToWorld.elements;
    e[12] += delta.x;
    e[13] += delta.y;
    e[14] += delta.z;
}

export function projectDeltaToWorkingPlane(delta: Vector3, workingPlaneNormal: Vector3): void {
    delta.addScaledVector(workingPlaneNormal, -delta.dot(workingPlaneNormal));
}

/**
 * Returns true when the selected instances span more than one wall alignment,
 * meaning a rigid-group drag should be used instead of per-wall constraint.
 */
export function isMixedAlignmentSelection(
    selectedInstanceIds: string[],
    dragRefsMap: Map<string, ArticleDragReference>
): boolean {
    const firstAlignment = new Vector3();
    const currentAlignment = new Vector3();
    let hasFirst = false;

    for (const instanceId of selectedInstanceIds) {
        const articleId = instanceToArticleIdMap.get(instanceId);
        const dragRef = articleId ? dragRefsMap.get(articleId) : undefined;
        const instanceData = dragRef?.articleRef.current?.articleInstances[instanceId];
        if (!instanceData) {
            continue;
        }

        if (!getAnchorAlignment(instanceData, hasFirst ? currentAlignment : firstAlignment)) {
            continue;
        }

        if (!hasFirst) {
            hasFirst = true;
            continue;
        }

        if (Math.abs(firstAlignment.dot(currentAlignment)) < ALIGNMENT_PARALLEL_THRESHOLD) {
            return true;
        }
    }

    return false;
}

export function isTopBottomCameraView(cameraDirection: Vector3): boolean {
    return Math.abs(cameraDirection.z) > TOP_BOTTOM_VIEW_THRESHOLD;
}

export function alignSelectionToCursorPoint(
    state: PlacementState,
    dragPoint: Vector3,
    snapAnchorInstanceId: string | undefined,
    constraintNormal: Vector3 | null = null
): boolean {
    const selectedInstanceIds = Object.keys(state.articleInstanceSelections);
    if (selectedInstanceIds.length === 0) {
        return false;
    }

    updateInstanceToArticleIdMap(selectedInstanceIds, state);

    const dragRefsMap = state.articleDragRefsMap.current;
    const anchorId =
        snapAnchorInstanceId && selectedInstanceIds.includes(snapAnchorInstanceId)
            ? snapAnchorInstanceId
            : selectedInstanceIds[0];

    const { anchorArticleId, anchorInstanceData } = getAnchorDragContext(anchorId, dragRefsMap);
    const anchorInstance =
        anchorInstanceData ??
        (anchorArticleId ? state.articles[anchorArticleId]?.articleInstances[anchorId] : undefined);

    if (!anchorInstance || !getAnchorPosition(anchorInstance, anchorPosition)) {
        return false;
    }

    let moved = false;
    const isTopBottomView = isTopBottomCameraView(raycastPlane.normal);

    targetPosition.copy(anchorPosition);

    const insertionPoint =
        state.isDragNewArticleActiveRef.current && anchorArticleId
            ? state.computeArticleInsertionPoint(anchorArticleId, anchorInstance)
            : null;
    if (state.isDragNewArticleActiveRef.current) {
        if (!insertionPoint) {
            return false;
        }
        targetPosition.copy(insertionPoint);
    } else if (insertionPoint) {
        targetPosition.copy(insertionPoint);
    }

    dragDelta.subVectors(dragPoint, targetPosition);

    if (state.isZAxisDragActiveRef.current) {
        dragDelta.x = 0;
        dragDelta.y = 0;
    }

    if (isTopBottomView) {
        dragDelta.z = 0;
    }

    if (constraintNormal) {
        projectDeltaToWorkingPlane(dragDelta, constraintNormal);
    }

    if (dragDelta.x === 0 && dragDelta.y === 0 && dragDelta.z === 0) {
        return false;
    }

    const hasPerInstanceOffsets = perInstanceDragOffsets.size > 0;

    for (const instanceId of selectedInstanceIds) {
        const { anchorArticleId: instanceArticleId, anchorInstanceData: instanceFromRef } = getAnchorDragContext(
            instanceId,
            dragRefsMap
        );
        const instanceData =
            instanceFromRef ??
            (instanceArticleId ? state.articles[instanceArticleId]?.articleInstances[instanceId] : undefined);
        if (!instanceData) {
            continue;
        }

        let delta = dragDelta;

        // Compute per-instance delta
        if (hasPerInstanceOffsets && instanceId !== anchorId) {
            const grabOffset = perInstanceDragOffsets.get(instanceId);
            if (grabOffset && getAnchorPosition(instanceData, perInstancePosition)) {
                instanceDragDelta.copy(dragPoint).sub(grabOffset).sub(perInstancePosition);
                delta = instanceDragDelta;

                if (state.isZAxisDragActiveRef.current) {
                    delta.x = 0;
                    delta.y = 0;
                }
                if (isTopBottomView) {
                    delta.z = 0;
                }

                if (getAnchorAlignment(instanceData, perInstanceAlignment)) {
                    projectDeltaToWorkingPlane(delta, perInstanceAlignment);
                } else if (constraintNormal) {
                    projectDeltaToWorkingPlane(delta, constraintNormal);
                }
            }
        }

        translateInstanceParts(instanceData, delta);

        // Keep cached snap colliders in sync
        const cached = dragColliderCache.get(instanceId);
        if (cached) {
            for (const p of cached) {
                p.add(delta);
            }
        }
        moved = true;
    }

    return moved;
}

export function placeInstanceOnWall(
    instanceId: string,
    articleId: string | null,
    state: PlacementState,
    dragRefsMap: Map<string, ArticleDragReference>
): void {
    const poiIndex = state.poiSelection.current.index;
    if (!instanceId || !articleId || poiIndex === undefined) {
        return;
    }

    // On top/bottom view insertion, snap the article to the bottom of the currently selected wall
    const articleDragRef = dragRefsMap.get(articleId);
    const instance = articleDragRef?.articleRef.current?.articleInstances[instanceId];
    const bottomZ = state.wallBottomZ;

    if (bottomZ !== undefined && instance && articleDragRef?.articleRef.current?.articleRepresentations) {
        const colliders = state.computeInstanceColliders(
            instanceId,
            instance,
            articleDragRef?.articleRef.current?.articleRepresentations,
            poiIndex
        );

        let newZ = bottomZ;
        if (colliders.length > 0) {
            const maxZ = Math.max(...colliders.map((c) => c.position.z));
            const minZ = Math.min(...colliders.map((c) => c.position.z));
            const height = maxZ - minZ;
            newZ = bottomZ + height / 2;
        }

        // Update z position of the instance parts and the raycast plane anchor point
        const dragInstance = articleDragRef?.articleRef.current?.articleInstances?.[instanceId];
        if (dragInstance) {
            if (state.isZPositionUpdatePending && state.faceSelectionRef.current) {
                boundingBoxHelper.setFromObject(state.faceSelectionRef.current);
                boundingBoxHelper.getCenter(wallCenter);
            }
            const parts = Object.values(dragInstance.parts);
            for (const part of parts) {
                if (state.isZPositionUpdatePending) {
                    part.position.x = wallCenter.x;
                    part.position.y = wallCenter.y;
                }
                part.position.z = newZ;
            }

            initialPoint.set(parts[0].position.x, parts[0].position.y, parts[0].position.z);
            raycastPlane.setFromNormalAndCoplanarPoint(raycastPlane.normal, initialPoint);
        }

        if (state.isZPositionUpdatePending) {
            const updatedArticleInstances: Array<{
                articleId: string;
                instanceId: string;
                instance: ArticleInstance;
            }> = [];

            updatedArticleInstances.push({ articleId, instanceId, instance: dragInstance });
            state.updateArticleInstances(updatedArticleInstances, false);
        }
    }
}

/* drag settings */
export function handleMarkerVisibility(markerRef: RefObject<Mesh | null>, isVisible: boolean) {
    if (markerRef.current) {
        markerRef.current.visible = isVisible;
    }
}

export function handleDragSettings(isDragStart: boolean, gl: RefObject<WebGLRenderer | null>) {
    if (gl.current) {
        gl.current.domElement.style.cursor = isDragStart ? 'move' : 'auto';
    }
}

/* instance grouping */
export function appendGroupedInstance(grouped: Map<string, string[]>, articleId: string, instanceId: string): void {
    const group = grouped.get(articleId);
    if (group) {
        group.push(instanceId);
    } else {
        grouped.set(articleId, [instanceId]);
    }
}

export function groupSelectedInstancesByArticle(selectedInstanceIds: string[]): Map<string, string[]> {
    const grouped = new Map<string, string[]>();
    for (const instanceId of selectedInstanceIds) {
        const articleId = instanceToArticleIdMap.get(instanceId);
        if (!articleId) {
            continue;
        }
        appendGroupedInstance(grouped, articleId, instanceId);
    }
    return grouped;
}

/* mappings */
export function updateInstanceToArticleIdMap(selectedInstanceIds: string[], state: PlacementState): void {
    for (const instanceId of selectedInstanceIds) {
        if (instanceToArticleIdMap.has(instanceId)) {
            continue;
        }
        let resolvedArticleId = state.selectedArticleId ?? undefined;
        if (!resolvedArticleId || !(instanceId in (state.articles[resolvedArticleId]?.articleInstances ?? {}))) {
            resolvedArticleId = Object.keys(state.articles).find(
                (articleId) => instanceId in state.articles[articleId].articleInstances
            );
        }
        if (resolvedArticleId) {
            instanceToArticleIdMap.set(instanceId, resolvedArticleId);
        }
    }
}

export function preUpdateInstanceToArticleIdMap(
    entries: ReadonlyArray<{ instanceId: string; articleId: string }>
): void {
    for (const { instanceId, articleId } of entries) {
        instanceToArticleIdMap.set(instanceId, articleId);
    }
}

export function updateInstanceToPoiIndexMap(selectedInstanceIds: string[], state: PlacementState): void {
    for (const instanceId of selectedInstanceIds) {
        if (instanceToPoiIndexMap.has(instanceId)) {
            continue;
        }
        const articleId = instanceToArticleIdMap.get(instanceId);
        if (!articleId) {
            continue;
        }
        for (let i = 0; i < state.pois.length; i++) {
            const poiArticles = state.pois[i].articles?.[articleId];
            if (Array.isArray(poiArticles) && poiArticles.includes(instanceId)) {
                instanceToPoiIndexMap.set(instanceId, i);
                break;
            }
        }
    }
}

export function hasAnyRegisteredDragRefs(
    selectedInstanceIds: string[],
    dragRefsMap: Map<string, ArticleDragReference>
): boolean {
    return (
        selectedInstanceIds.length > 0 &&
        selectedInstanceIds.some((id) => {
            const articleId = instanceToArticleIdMap.get(id);
            return articleId !== undefined && dragRefsMap.has(articleId);
        })
    );
}

/* dragging box */
export function scheduleDraggingBoxComputation(
    selectedInstanceIds: string[],
    anchorId: string,
    dragRefsMap: Map<string, ArticleDragReference>,
    draggingBox: DraggingBox,
    placementState?: PlacementState
): void {
    const anchorArticleId = instanceToArticleIdMap.get(anchorId);
    const anchorDragRef = anchorArticleId ? dragRefsMap.get(anchorArticleId) : undefined;
    const anchorInstanceData = anchorDragRef?.articleRef.current?.articleInstances[anchorId];

    const articleTypesMap = new Map<string, DraggingBoxWorkerInput['articleTypes'][0]>();
    const instancesForWorker: DraggingBoxWorkerInput['instances'] = [];
    const buildArticleTypeId = (articleId: string, partName: string) => `${articleId}::${partName}`;

    for (const instanceId of selectedInstanceIds) {
        const articleId = instanceToArticleIdMap.get(instanceId);
        const dragRef = articleId ? dragRefsMap.get(articleId) : undefined;
        if (!dragRef) {
            continue;
        }

        // Use the geometry bounding box from the BatchedMesh geometry slots.
        if (placementState) {
            const slots = placementState.articleGeometrySlots.get(articleId!);
            const firstSlotId = slots?.physicalSlots[0]?.slotId;
            const batchMesh = placementState.batchedMeshes.physical;
            if (firstSlotId !== undefined && batchMesh) {
                const bbox = new Box3();
                batchMesh.getBoundingBoxAt(firstSlotId, bbox);
                if (!bbox.isEmpty()) {
                    const articleTypeId = buildArticleTypeId(articleId!, 'batch');
                    articleTypesMap.set(articleTypeId, {
                        id: articleTypeId,
                        matrixWorld: Array.from(new Matrix4Three().elements),
                        geomMin: { x: bbox.min.x, y: bbox.min.y, z: bbox.min.z },
                        geomMax: { x: bbox.max.x, y: bbox.max.y, z: bbox.max.z }
                    });
                }
            }
        }

        const instanceData = dragRef.articleRef.current?.articleInstances[instanceId];
        if (instanceData) {
            const batchArticleTypeId = buildArticleTypeId(articleId!, 'batch');

            for (const [partName, part] of Object.entries(instanceData.parts)) {
                const articleTypeId = articleTypesMap.has(batchArticleTypeId)
                    ? batchArticleTypeId
                    : buildArticleTypeId(articleId!, partName);
                if (!articleTypesMap.has(articleTypeId)) {
                    continue;
                }

                instancesForWorker.push({
                    position: {
                        x: part.position.x,
                        y: part.position.y,
                        z: part.position.z
                    },
                    alignment: {
                        x: part.alignment.x,
                        y: part.alignment.y,
                        z: part.alignment.z
                    },
                    rotation: part.rotation,
                    articleTypeId
                });
            }
        }
    }

    if (!anchorInstanceData || articleTypesMap.size === 0 || instancesForWorker.length === 0) {
        return;
    }

    // Use midpoint of all selected instances as anchor position so the
    // local coordinate frame is centered on the selection (matches transformation mesh).
    const midpoint = new Vector3();
    let midCount = 0;
    for (const instanceId of selectedInstanceIds) {
        const articleId = instanceToArticleIdMap.get(instanceId);
        const dragRef = articleId ? dragRefsMap.get(articleId) : undefined;
        const instData = dragRef?.articleRef.current?.articleInstances[instanceId];
        if (instData) {
            const pos = new Vector3();
            if (getAnchorPosition(instData, pos)) {
                midpoint.add(pos);
                midCount++;
            }
        }
    }

    const anchorPositionForWorker = new Vector3();
    const anchorAlignment = new Vector3();
    if (midCount > 0) {
        midpoint.divideScalar(midCount);
        anchorPositionForWorker.copy(midpoint);
    } else {
        getAnchorPosition(anchorInstanceData, anchorPositionForWorker);
    }
    getAnchorAlignment(anchorInstanceData, anchorAlignment);

    let anchorRotation = 0;
    const anchorFirstPart = Object.values(anchorInstanceData.parts)[0];
    if (anchorFirstPart) {
        anchorRotation = anchorFirstPart.rotation;
    }

    draggingBoxStatus.isPending = true;

    postDraggingBoxTask({
        type: 'computeDraggingBox',
        articleTypes: Array.from(articleTypesMap.values()),
        instances: instancesForWorker,
        anchor: {
            position: { x: anchorPositionForWorker.x, y: anchorPositionForWorker.y, z: anchorPositionForWorker.z },
            alignment: { x: anchorAlignment.x, y: anchorAlignment.y, z: anchorAlignment.z },
            rotation: anchorRotation
        }
    })
        .then((result) => {
            draggingBoxStatus.isPending = false;
            draggingBoxStatus.isComputed = true;

            if (!result.isEmpty) {
                draggingBox.boundingBox.set(
                    new Vector3(result.localMin.x, result.localMin.y, result.localMin.z),
                    new Vector3(result.localMax.x, result.localMax.y, result.localMax.z)
                );
                draggingBox.localToWorld.fromArray(result.localToWorld);
            }

            invalidate();
        })
        .catch((e) => {
            if (e instanceof DOMException && e.name === 'AbortError') {
                draggingBoxStatus.isPending = false;
                return;
            }

            draggingBoxStatus.isPending = false;
            draggingBoxStatus.isComputed = true;

            // eslint-disable-next-line no-console
            console.error('DraggingBox worker error', e);
        });
}

/**
 * Seeds per-instance wall planes and intersection points for all selected instances.
 */
export function initializePerInstancePlanesAndIntersections(
    selectedInstanceIds: string[],
    dragRefsMap: Map<string, ArticleDragReference>,
    eventRay: Ray,
    perInstancePlanes: Map<string, Plane>,
    perInstancePrevIntersections: Map<string, Vector3>
) {
    const initAlign = new Vector3();
    const initPos = new Vector3();
    for (const instanceId of selectedInstanceIds) {
        const articleId = instanceToArticleIdMap.get(instanceId);
        if (!articleId) {
            continue;
        }
        const dragRef = dragRefsMap.get(articleId);
        if (!dragRef || !dragRef.articleRef.current) {
            continue;
        }
        const instanceData = dragRef.articleRef.current.articleInstances[instanceId];
        if (!instanceData) {
            continue;
        }
        if (getAnchorAlignment(instanceData, initAlign) && getAnchorPosition(instanceData, initPos)) {
            const wallPlane = new Plane().setFromNormalAndCoplanarPoint(initAlign, initPos);
            perInstancePlanes.set(instanceId, wallPlane);
            const initPt = new Vector3();
            if (eventRay.intersectPlane(wallPlane, initPt)) {
                perInstancePrevIntersections.set(instanceId, initPt);
            }
        }
    }
}

/**
 * Applies a movement delta to a single instance's parts and syncs its collider cache.
 */
export function applyMoveToInstance(instanceId: string, instanceData: ArticleInstance, delta: Vector3): void {
    translateInstanceParts(instanceData, delta);
    const cached = dragColliderCache.get(instanceId);
    if (cached) {
        for (const point of cached) {
            point.add(delta);
        }
    }
}

/**
 * Computes the per-instance wall-plane movement delta for object-based dragging.
 * Writes the result into `outDelta` and returns true if the delta is non-zero.
 * Updates perInstancePrevIntersections as a side effect.
 */
export function computePerInstanceWallDelta(
    instanceId: string,
    eventRay: Ray,
    isTopBottomViewDrag: boolean,
    fallbackDelta: Vector3,
    outDelta: Vector3
): boolean {
    const wallPlane = perInstancePlanes.get(instanceId);
    const prevPt = perInstancePrevIntersections.get(instanceId);

    if (wallPlane && !isTopBottomViewDrag && eventRay.intersectPlane(wallPlane, wallIntersectionPoint)) {
        if (prevPt) {
            outDelta.subVectors(wallIntersectionPoint, prevPt);
            prevPt.copy(wallIntersectionPoint);
        } else {
            perInstancePrevIntersections.set(instanceId, wallIntersectionPoint.clone());
            outDelta.set(0, 0, 0);
        }
    } else {
        outDelta.copy(fallbackDelta);
        if (wallPlane) {
            projectDeltaToWorkingPlane(outDelta, wallPlane.normal);
        }

        // Keep selection synchronized with cursor after leaving the camera frustum
        if (prevPt && (outDelta.x !== 0 || outDelta.y !== 0 || outDelta.z !== 0)) {
            prevPt.add(outDelta);
        }
    }

    return outDelta.x !== 0 || outDelta.y !== 0 || outDelta.z !== 0;
}

/**
 * Projects and applies a snap offset to all selected instances, respecting per-instance wall normals.
 * `rigidInstances` can be:
 *   - `true`  → all instance get dragged as a rigid group by applying uniform snap offsets (dragging-box / groups)
 *   - `false` → all instances get dragged individual along their own planes
 *   - `Set<string>` → only instances included in the Set get uniform snap offsets - others get individual offsets
 */
export function applySnapOffsetToInstances(
    selectedInstanceIds: string[],
    dragRefsMap: Map<string, ArticleDragReference>,
    snapOffset: Vector3,
    perInstancePlanes: Map<string, Plane>,
    rigidInstances: Set<string> | boolean = false
) {
    for (const instanceId of selectedInstanceIds) {
        const articleId = instanceToArticleIdMap.get(instanceId);
        if (!articleId) {
            continue;
        }
        const dragRef = dragRefsMap.get(articleId);
        if (!dragRef || !dragRef.articleRef.current) {
            continue;
        }
        const instanceData = dragRef.articleRef.current.articleInstances[instanceId];
        if (!instanceData) {
            continue;
        }

        const isRigid = typeof rigidInstances === 'boolean' ? rigidInstances : rigidInstances.has(instanceId);

        if (isRigid) {
            // Apply uniform snap offset - not per instance
            applyMoveToInstance(instanceId, instanceData, snapOffset);
        } else {
            const plane = perInstancePlanes.get(instanceId);
            if (plane) {
                projectedSnapDelta.copy(snapOffset);
                projectDeltaToWorkingPlane(projectedSnapDelta, plane.normal);
                applyMoveToInstance(instanceId, instanceData, projectedSnapDelta);
            } else {
                applyMoveToInstance(instanceId, instanceData, snapOffset);
            }
        }
    }
}

/* Clamp Placement Helpers */
export async function waitForManualConnectorInsertion(articleId: string | null): Promise<boolean> {
    if (!articleId) {
        return false;
    }

    const currentState = usePlacementStore.getState();
    const poiIndex = currentState.poiSelection.current.index;
    const articleNumber = articleId ? currentState.articles[articleId]?.articleNumber : null;

    if (!articleNumber || !isFramiClamp(articleNumber)) {
        return true;
    }

    if (poiIndex === undefined) {
        return true;
    }

    await currentState.prepareManualConnectorDrag?.({
        articleId,
        articleNumber,
        poiIndex
    });

    const state = usePlacementStore.getState();
    return (
        state.selectedArticleId === articleId &&
        state.connectorSnapData.articleId === articleId &&
        !state.connectorSnapData.isLoading &&
        state.connectorSnapData.candidates.length > 0 &&
        state.connectorSnapData.plane !== null
    );
}

export function validateConnectorInsertionStart(isDoubleClick: boolean, articleId: string | null): void {
    void (async () => {
        const canStartInsertion = await waitForManualConnectorInsertion(articleId);
        if (canStartInsertion) {
            usePlacementStore.getState().handleOnInsertArticleStart(isDoubleClick);
        }
    })();
}

export async function waitForManualCornerInsertion(articleId: string | null): Promise<boolean> {
    if (!articleId) {
        return false;
    }

    const currentState = usePlacementStore.getState();
    const poiIndex = currentState.poiSelection.current.index;
    const articleNumber = articleId ? currentState.articles[articleId]?.articleNumber : null;

    if (!articleNumber) {
        return true;
    }

    if (poiIndex === undefined) {
        return true;
    }

    usePlacementStore.setState({ waitForPlacementData: true });
    await currentState.prepareManualCornerInsertion?.({
        articleId,
        articleNumber,
        poiIndex
    });

    const state = usePlacementStore.getState();
    return (
        state.selectedArticleId === articleId &&
        state.connectorSnapData.articleId === articleId &&
        !state.connectorSnapData.isLoading &&
        state.connectorSnapData.candidates.length > 0 &&
        state.connectorSnapData.plane !== null
    );
}

export function validateCornerInsertionStart(isDoubleClick: boolean, articleId: string | null): void {
    void (async () => {
        const canStartInsertion = await waitForManualCornerInsertion(articleId);
        if (canStartInsertion) {
            usePlacementStore.getState().handleOnInsertArticleStart(isDoubleClick);
        }
    })();
}

function waitForArticleParts(articleId: string): Promise<boolean> {
    const state = usePlacementStore.getState();
    if ((state.articles[articleId]?.partNames.length ?? 0) > 0) {
        return Promise.resolve(true);
    }

    usePlacementStore.setState({ waitForPlacementData: true });

    return new Promise((resolve) => {
        const unsubscribe = usePlacementStore.subscribe((currentState) => {
            const isSelected = currentState.selectedArticleId === articleId;
            const hasParts = (currentState.articles[articleId]?.partNames.length ?? 0) > 0;

            if (hasParts || !isSelected) {
                unsubscribe();
                usePlacementStore.setState({ waitForPlacementData: false });
                resolve(hasParts && isSelected);
            }
        });
    });
}

export function validateArticleInsertionStart(isDoubleClick: boolean, articleId: string | null): void {
    if (!articleId) {
        return;
    }

    void waitForArticleParts(articleId).then((canStartInsertion) => {
        if (canStartInsertion) {
            usePlacementStore.getState().handleOnInsertArticleStart(isDoubleClick);
        }
    });
}

export function handleOnValidateInsertion(isDoubleClick: boolean, articleId: string | null): void {
    const state = usePlacementStore.getState();

    const isTopBottomView = isTopBottomCameraView(raycastPlane.normal);
    const articleNumber = articleId ? state.articles[articleId]?.articleNumber : null;
    const isConnectorInsertion = isFramiClamp(articleNumber);
    const isCornerInsertion = state.poiSelection.current.filterType === POIFilterType.Corner;

    if (isConnectorInsertion) {
        if (!isTopBottomView) {
            validateConnectorInsertionStart(isDoubleClick, articleId);
        } else {
            toastNotifications.information({ title: t('info'), message: t('infoClampPlacement') });
        }
    }

    if (isCornerInsertion) {
        validateCornerInsertionStart(isDoubleClick, articleId);
    }

    if (!isConnectorInsertion && !isCornerInsertion) {
        validateArticleInsertionStart(isDoubleClick, articleId);
    }
}

export function findNearestConnectorSnapCandidate(
    source: Vector3,
    candidates: PartPlacement[],
    maxDistance: number = Number.POSITIVE_INFINITY
): PartPlacement | null {
    let nearest: PartPlacement | null = null;
    let nearestDistance = maxDistance;

    for (const candidate of candidates) {
        const dx = candidate.position.x - source.x;
        const dy = candidate.position.y - source.y;
        const dz = candidate.position.z - source.z;

        const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);

        if (distance < nearestDistance) {
            nearestDistance = distance;
            nearest = candidate;
        }
    }

    return nearest;
}

export function applyRotationToSelectedInstances(
    selectedInstanceIds: string[],
    dragRefsMap: PlacementState['articleDragRefsMap']['current'],
    rotation: number
): void {
    for (const instanceId of selectedInstanceIds) {
        const articleId = instanceToArticleIdMap.get(instanceId);
        const articleDragRef = articleId ? dragRefsMap.get(articleId) : undefined;
        const instanceData = articleDragRef?.articleRef.current?.articleInstances[instanceId];
        if (!instanceData) {
            continue;
        }

        for (const part of Object.values(instanceData.parts)) {
            part.rotation = rotation;
        }
    }
}

export function getConnectorArticleId(state: PlacementState, anchorInstanceId?: string): string | null {
    if (state.isDragNewArticleStartRef.current || state.isDragNewArticleActiveRef.current) {
        return state.selectedArticleId;
    }

    if (anchorInstanceId) {
        const articleId = instanceToArticleIdMap.get(anchorInstanceId) ?? getArticleId(anchorInstanceId, state);
        if (articleId) {
            return articleId;
        }
    }

    return state.selectedArticleId;
}

export function getConnectorSnapPlane(state: PlacementState, activeArticleId: string | null) {
    return activeArticleId !== null &&
        state.connectorSnapData.articleId === activeArticleId &&
        state.connectorSnapData.candidates.length > 0
        ? state.connectorSnapData.plane
        : null;
}

export function getConnectorSnapSource(
    dragPoint: Vector3,
    manualInsertionPlane: Plane | null,
    workingPlane: Plane | null
): Vector3 {
    adjustedSnapSource.copy(dragPoint);
    if (manualInsertionPlane) {
        manualInsertionPlane.projectPoint(adjustedSnapSource, adjustedSnapSource);
    } else if (workingPlane) {
        workingPlane.projectPoint(adjustedSnapSource, adjustedSnapSource);
    }
    return adjustedSnapSource;
}

export function getDragConstraintNormal(
    isConnectorDrag: boolean,
    manualInsertionPlane: Plane | null,
    workingPlane: Plane | null,
    sharedAlignmentNormal: Vector3,
    isMixedAlignmentDrag: boolean
): Vector3 | null {
    // Connector + top view → use placement plane
    if (isConnectorDrag && manualInsertionPlane) {
        return manualInsertionPlane.normal;
    }

    // Top/bottom view (generic)
    if (isTopBottomCameraView(raycastPlane.normal) && workingPlane) {
        return workingPlane.normal;
    }

    // Default wall alignment
    if (!isMixedAlignmentDrag && sharedAlignmentNormal.lengthSq() > 0) {
        return sharedAlignmentNormal;
    }

    return null;
}

export function getPreferredInsertionPlane(state: PlacementState, manualInsertionPlane: Plane | null): Plane | null {
    return manualInsertionPlane ?? state.workingPlane ?? null;
}

export function handleOnInsertionStart(
    isDoubleClick: boolean,
    articleId: string | null,
    t: TFunction<'translations', undefined>
) {
    const interactionMode = usePlacementStore.getState().interactionMode;
    if (interactionMode === InteractionMode.POI_NAVIGATION) {
        usePlacementStore.getState().setIsDragNewArticleStartRef(false);
        requestAnimationFrame(() => {
            toastNotifications.information({ title: t('info'), message: t('infoDisabledPlacement') });
        });
    } else {
        handleOnValidateInsertion(isDoubleClick, articleId);
    }
}

/* Handle POI selection change during auto drag */

export const retargetAnchorPoint = new Vector3();
export const retargetNormal = new Vector3();
export const retargetProjectedPoint = new Vector3();

const retargetQuaternion = new Quaternion();
const retargetPlane = new Plane();
const retargetWorldUp = new Vector3(0, 0, 1);
const retargetWorldRight = new Vector3(1, 0, 0);
const retargetAxis = new Vector3();
const retargetSourceAlignment = new Vector3();
const retargetPartQuaternion = new Quaternion();
const retargetNewAlignQuaternion = new Quaternion();
const retargetRollQuaternion = new Quaternion();
const retargetNewAlignment = new Vector3();
const retargetPreviousQuaternion = new Quaternion();
const retargetCurrentQuaternion = new Quaternion();
const retargetDeltaQuaternion = new Quaternion();
const retargetPartDeltaQuaternion = new Quaternion();
const retargetAlignmentQuaternion = new Quaternion();
const retargetAlignment = new Vector3();

function updateQuaternionOnPoiChange(sourceAlignment: Vector3, targetNormal: Vector3): void {
    const alignmentDot = sourceAlignment.dot(targetNormal);

    switch (true) {
        case alignmentDot > 1 - EPSILON:
            retargetQuaternion.identity();
            return;
        case alignmentDot < -1 + EPSILON:
            retargetAxis.copy(retargetWorldUp).addScaledVector(sourceAlignment, -retargetWorldUp.dot(sourceAlignment));
            if (retargetAxis.lengthSq() < EPSILON) {
                retargetAxis
                    .copy(retargetWorldRight)
                    .addScaledVector(sourceAlignment, -retargetWorldRight.dot(sourceAlignment));
            }
            retargetQuaternion.setFromAxisAngle(retargetAxis.normalize(), Math.PI);
            return;
        default:
            retargetQuaternion.setFromUnitVectors(sourceAlignment, targetNormal);
    }
}

export function updateInstancesOnPoiChange(state: PlacementState, selectedInstanceIds: string[]): void {
    const grouped = groupSelectedInstancesByArticle(selectedInstanceIds);
    for (const [articleId, instanceIds] of grouped) {
        const dragRef = state.articleDragRefsMap.current.get(articleId);
        if (dragRef) {
            state.updateInstanceMatrices(true, dragRef.articleRef, false, instanceIds);
        }
    }
}

export function updateAlignmentOnPoiChange(
    state: PlacementState,
    selectedInstanceIds: string[],
    targetNormal: Vector3,
    anchorPoint: Vector3
): void {
    const dragRefsMap = state.articleDragRefsMap.current;
    updateInstanceToArticleIdMap(selectedInstanceIds, state);

    for (const instanceId of selectedInstanceIds) {
        const articleId = instanceToArticleIdMap.get(instanceId);
        const instanceData = articleId
            ? dragRefsMap.get(articleId)?.articleRef.current?.articleInstances[instanceId]
            : undefined;
        if (!instanceData) {
            continue;
        }

        const parts = Object.values(instanceData.parts);
        const sourceAlignment = parts[0]?.alignment;
        if (!sourceAlignment) {
            continue;
        }

        retargetSourceAlignment.copy(sourceAlignment).normalize();
        updateQuaternionOnPoiChange(retargetSourceAlignment, targetNormal);

        for (const part of parts) {
            updateFullQuaternion(part.alignment, part.rotation, retargetPartQuaternion);
            retargetPartQuaternion.premultiply(retargetQuaternion);

            part.position.sub(anchorPoint).applyQuaternion(retargetQuaternion).add(anchorPoint);
            retargetNewAlignment.copy(part.alignment).applyQuaternion(retargetQuaternion).normalize();
            part.alignment.copy(retargetNewAlignment);

            updateFullQuaternion(retargetNewAlignment, 0, retargetNewAlignQuaternion);
            retargetRollQuaternion.copy(retargetNewAlignQuaternion).invert();
            retargetRollQuaternion.premultiply(retargetPartQuaternion);
            part.rotation = decomposeRollAroundAxis(retargetRollQuaternion, retargetNewAlignment);
        }
    }
}

export function getAnchorPointOnPoiChange(
    state: PlacementState,
    selectedInstanceIds: string[],
    target: Vector3,
    snapAnchorInstanceId: string | undefined
): boolean {
    const anchorId =
        snapAnchorInstanceId && selectedInstanceIds.includes(snapAnchorInstanceId)
            ? snapAnchorInstanceId
            : selectedInstanceIds[0];
    if (!anchorId) {
        return false;
    }

    updateInstanceToArticleIdMap(selectedInstanceIds, state);
    const articleId = instanceToArticleIdMap.get(anchorId);
    const instanceData = articleId
        ? state.articleDragRefsMap.current.get(articleId)?.articleRef.current?.articleInstances[anchorId]
        : undefined;
    if (!instanceData) {
        return false;
    }

    const insertionPoint = articleId ? state.computeArticleInsertionPoint(articleId, instanceData) : null;
    if (insertionPoint) {
        target.copy(insertionPoint);
        return true;
    }

    return getAnchorPosition(instanceData, target);
}

export function getWallTargetPoint(state: PlacementState, targetPlane: Plane, target: Vector3): void {
    const raycaster = state.mainRaycasterRef.current;
    if (raycaster?.ray.intersectPlane(targetPlane, target)) {
        return;
    }

    const sourcePoint = state.pointerPositionRef.current ?? dragAnchorPositionRef.current ?? retargetAnchorPoint;
    targetPlane.projectPoint(sourcePoint, target);
}

export function getWallTargetPlane(state: PlacementState, poiIndex: number): Plane | null {
    const poi = state.pois[poiIndex]?.poi;
    if (!poi || !isPlanarFacePoi(poi)) {
        return null;
    }

    const firstPoint = poi.formFace.outerWire.edges[0]?.startPoint;
    if (!firstPoint) {
        return null;
    }

    retargetNormal.set(poi.formFace.normal.x, poi.formFace.normal.y, poi.formFace.normal.z).normalize();
    retargetAnchorPoint.set(firstPoint.x, firstPoint.y, firstPoint.z);
    return retargetPlane.setFromNormalAndCoplanarPoint(retargetNormal, retargetAnchorPoint);
}

export function updateReferencesOnPoiChange(
    state: PlacementState,
    selectedInstanceIds: string[],
    poiIndex: number
): void {
    const relatedPoiGuids = getRelatedPoiIndices(state.pois, poiIndex)
        .map((relatedPoiIndex) => state.pois[relatedPoiIndex]?.poi.poiGuid)
        .filter((poiGuid): poiGuid is string => Boolean(poiGuid));
    if (relatedPoiGuids.length === 0) {
        return;
    }

    activePoiGuids.clear();
    relatedPoiGuids.forEach((poiGuid) => activePoiGuids.add(poiGuid));
    updateInstanceToArticleIdMap(selectedInstanceIds, state);

    for (const instanceId of selectedInstanceIds) {
        instanceToPoiIndexMap.set(instanceId, poiIndex);
        dragColliderCache.delete(instanceId);
        const articleId = instanceToArticleIdMap.get(instanceId);
        const instance = articleId
            ? state.articleDragRefsMap.current.get(articleId)?.articleRef.current?.articleInstances[instanceId]
            : undefined;
        if (instance) {
            instance.poiGuids = relatedPoiGuids;
        }
    }
}

export function getCornerPartDelta(
    previousAnchorPart: Part | undefined,
    currentAnchorPart: Part | undefined,
    fallbackDelta: Quaternion
): Quaternion {
    const partDelta = retargetPartDeltaQuaternion;
    if (!previousAnchorPart || !currentAnchorPart) {
        return partDelta.copy(fallbackDelta);
    }

    updateFullQuaternion(previousAnchorPart.alignment, previousAnchorPart.rotation, retargetPreviousQuaternion);
    updateFullQuaternion(currentAnchorPart.alignment, currentAnchorPart.rotation, retargetCurrentQuaternion);
    return partDelta.copy(retargetPreviousQuaternion).invert().premultiply(retargetCurrentQuaternion);
}

export function applyCornerPartTransform(
    part: Part,
    previousAnchorPart: Part | undefined,
    currentAnchorPart: Part | undefined,
    previousAnchorPoint: Vector3,
    currentAnchorPoint: Vector3,
    fallbackDelta: Quaternion
): void {
    const partDelta = getCornerPartDelta(previousAnchorPart, currentAnchorPart, fallbackDelta);
    const pivotFrom = previousAnchorPart?.position ?? previousAnchorPoint;
    const pivotTo = currentAnchorPart?.position ?? currentAnchorPoint;

    updateFullQuaternion(part.alignment, part.rotation, retargetPartQuaternion);
    retargetPartQuaternion.premultiply(partDelta);
    part.position.sub(pivotFrom).applyQuaternion(partDelta).add(pivotTo);

    retargetAlignment.copy(part.alignment).applyQuaternion(partDelta).normalize();
    part.alignment.copy(retargetAlignment);
    updateFullQuaternion(retargetAlignment, 0, retargetAlignmentQuaternion);
    retargetRollQuaternion.copy(retargetAlignmentQuaternion).invert();
    retargetRollQuaternion.premultiply(retargetPartQuaternion);
    part.rotation = decomposeRollAroundAxis(retargetRollQuaternion, retargetAlignment);
}

export function retargetSelectionAtCorner(
    state: PlacementState,
    selectedInstanceIds: string[],
    anchorInstanceId: string | undefined,
    previousAnchorParts: Record<string, Part>,
    currentAnchorParts: Record<string, Part>,
    previousAnchorPoint: Vector3,
    currentAnchorPoint: Vector3,
    deltaQuaternion: Quaternion
): void {
    for (const instanceId of selectedInstanceIds) {
        if (instanceId === anchorInstanceId) {
            continue;
        }

        const articleId = instanceToArticleIdMap.get(instanceId);
        const instanceData = articleId
            ? state.articleDragRefsMap.current.get(articleId)?.articleRef.current?.articleInstances[instanceId]
            : undefined;
        if (!instanceData) {
            continue;
        }

        for (const [partName, part] of Object.entries(instanceData.parts)) {
            const previousAnchorPart = previousAnchorParts[partName];
            const currentAnchorPart = currentAnchorParts[partName];
            applyCornerPartTransform(
                part,
                previousAnchorPart,
                currentAnchorPart,
                previousAnchorPoint,
                currentAnchorPoint,
                deltaQuaternion
            );
        }
    }
}

export async function updateCornerPlacementTarget(
    state: PlacementState,
    getState: () => PlacementState,
    selectedInstanceIds: string[],
    poiIndex: number
): Promise<boolean> {
    const anchorInstanceId = selectedInstanceIds[0];
    updateInstanceToArticleIdMap(selectedInstanceIds, state);
    const articleId = instanceToArticleIdMap.get(anchorInstanceId ?? '') ?? state.selectedArticleId;
    if (!articleId) {
        return false;
    }

    const prepareCornerInsertion = state.prepareManualCornerInsertion;
    if (!prepareCornerInsertion) {
        return false;
    }

    const hasPreviousAnchorPoint = getAnchorPointOnPoiChange(
        state,
        selectedInstanceIds,
        retargetAnchorPoint,
        anchorInstanceId
    );
    if (!hasPreviousAnchorPoint) {
        return false;
    }
    const previousAnchorPoint = retargetAnchorPoint.clone();

    const canPrepareCorner = await prepareCornerInsertion({
        articleId,
        articleNumber: state.articles[articleId]?.articleNumber ?? null,
        poiIndex
    });
    if (!canPrepareCorner) {
        return false;
    }

    const currentState = getState();
    const { connectorSnapData } = currentState;
    const canInsertOnCorner =
        connectorSnapData.articleId === articleId &&
        connectorSnapData.poiIndex === poiIndex &&
        connectorSnapData.plane !== null &&
        connectorSnapData.candidates.length > 0;
    if (!canInsertOnCorner) {
        return false;
    }

    const articleDragRef = currentState.articleDragRefsMap.current.get(articleId);
    const anchorInstanceData = anchorInstanceId
        ? articleDragRef?.articleRef.current?.articleInstances[anchorInstanceId]
        : undefined;
    if (!anchorInstanceData) {
        return false;
    }

    const previousAnchorParts = cloneInstanceParts(anchorInstanceData.parts);
    const previousAnchorPart = getAnchorPart({ ...anchorInstanceData, parts: previousAnchorParts });
    if (!previousAnchorPart) {
        return false;
    }
    updateFullQuaternion(previousAnchorPart.alignment, previousAnchorPart.rotation, retargetPreviousQuaternion);

    const currentAnchorParts = mapPartsArrayToRecord(connectorSnapData.candidates);
    anchorInstanceData.parts = currentAnchorParts;
    const hasCurrentAnchorPoint = getAnchorPointOnPoiChange(
        currentState,
        selectedInstanceIds,
        retargetProjectedPoint,
        anchorInstanceId
    );
    if (!hasCurrentAnchorPoint) {
        anchorInstanceData.parts = previousAnchorParts;
        return false;
    }

    const currentAnchorPart = getAnchorPart(anchorInstanceData);
    if (!currentAnchorPart) {
        anchorInstanceData.parts = previousAnchorParts;
        return false;
    }
    updateFullQuaternion(currentAnchorPart.alignment, currentAnchorPart.rotation, retargetCurrentQuaternion);
    retargetDeltaQuaternion.copy(retargetPreviousQuaternion).invert();
    retargetDeltaQuaternion.premultiply(retargetCurrentQuaternion);

    retargetSelectionAtCorner(
        currentState,
        selectedInstanceIds,
        anchorInstanceId,
        previousAnchorParts,
        currentAnchorParts,
        previousAnchorPoint,
        retargetProjectedPoint,
        retargetDeltaQuaternion
    );

    currentState.setPointerPositionRef(retargetProjectedPoint);
    updateInstancesOnPoiChange(currentState, selectedInstanceIds);
    updateReferencesOnPoiChange(currentState, selectedInstanceIds, poiIndex);

    return true;
}

export function updateWallPlacementTarget(
    state: PlacementState,
    selectedInstanceIds: string[],
    poiIndex: number,
    snapAnchorInstanceId: string | undefined
): boolean {
    const targetPlane = getWallTargetPlane(state, poiIndex);
    if (!targetPlane) {
        return false;
    }

    state.setWorkingPlane(targetPlane.clone());
    getWallTargetPoint(state, targetPlane, retargetProjectedPoint);
    raycastPlane.setFromNormalAndCoplanarPoint(retargetNormal, retargetProjectedPoint);
    state.setPointerPositionRef(retargetProjectedPoint);

    if (selectedInstanceIds.length === 0) {
        return true;
    }

    const hasRotationAnchor = getAnchorPointOnPoiChange(
        state,
        selectedInstanceIds,
        retargetAnchorPoint,
        snapAnchorInstanceId
    );
    updateAlignmentOnPoiChange(
        state,
        selectedInstanceIds,
        retargetNormal,
        hasRotationAnchor ? retargetAnchorPoint : retargetProjectedPoint
    );
    alignSelectionToCursorPoint(state, retargetProjectedPoint, snapAnchorInstanceId);
    syncDragStateOnPoiChange(selectedInstanceIds, targetPlane);
    updateInstancesOnPoiChange(state, selectedInstanceIds);
    updateReferencesOnPoiChange(state, selectedInstanceIds, poiIndex);

    return true;
}

export function computeWorkingPlaneData(geometry: BufferGeometry): WorkingPlaneData {
    const pos = new Vector3().fromBufferAttribute(geometry.attributes.position as BufferAttribute, 0);
    const normal = new Vector3().fromBufferAttribute(geometry.attributes.normal as BufferAttribute, 0);

    const ref = Math.abs(normal.dot(new Vector3(0, 0, 1))) > 0.99 ? new Vector3(1, 0, 0) : new Vector3(0, 0, 1);
    const quaternion = new Quaternion().setFromUnitVectors(ref, normal.clone().normalize());

    return { position: pos, normal, quaternion };
}
